'use client';

import React, { useCallback, useEffect, useRef } from 'react';
import { PricingDisplay } from './pricing-display';
import { ExportTools } from './export-tools';
import { ARPreview } from './ar-preview';

interface ConfigSummaryPanelProps {
  open: boolean;
  onClose: () => void;
  productId: string;
  productName: string;
  totalPrice: string | null;
  activeSelections: [string, unknown][];
  selections: Record<string, unknown>;
  resolveSelectionLabel: (key: string, value: unknown) => { label: string; display: string };
  onShare: () => void;
  onReset: () => void;
  onDownloadSummary: () => void;
  shareStatus: 'idle' | 'saving' | 'copied';
  modelUrl?: string;
  /** Narrow viewports: extra space below summary so it clears the docked config rail (px). */
  mobileRailClearancePx?: number;
}

export function ConfigSummaryPanel({
  open,
  onClose,
  productId,
  productName,
  totalPrice,
  activeSelections,
  selections,
  resolveSelectionLabel,
  onShare,
  onReset,
  onDownloadSummary,
  shareStatus,
  modelUrl,
  mobileRailClearancePx,
}: ConfigSummaryPanelProps) {
  const panelRef = useRef<HTMLDivElement>(null);

  const handleClickOutside = useCallback((e: MouseEvent) => {
    if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
      const bottomBar = document.getElementById('config-options');
      if (bottomBar?.contains(e.target as Node)) return;
      onClose();
    }
  }, [onClose]);

  useEffect(() => {
    if (!open) return;
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open, handleClickOutside]);

  if (!open) return null;

  const filteredSelections = activeSelections.filter(([, v]) => !Array.isArray(v));

  return (
    <div
      className="absolute bottom-0 left-0 right-0 z-40 flex justify-center px-2 pointer-events-none md:px-4"
      style={
        mobileRailClearancePx != null
          ? {
              paddingBottom: `calc(${mobileRailClearancePx}px + env(safe-area-inset-bottom, 0px))`,
            }
          : undefined
      }
    >
      <div
        ref={panelRef}
        className="pointer-events-auto mb-3 w-full max-w-lg rounded-2xl border border-zinc-200/60 bg-white/90 shadow-xl shadow-black/10 backdrop-blur-xl dark:border-zinc-700/60 dark:bg-zinc-900/90 dark:shadow-black/30 md:mb-[4.5rem]"
        style={{ animation: 'slideUp 200ms ease-out' }}
      >
        <div className="flex items-center justify-between px-4 pt-3 pb-2 border-b border-zinc-200/60 dark:border-zinc-700/60">
          <h3 className="text-sm font-semibold text-zinc-900 dark:text-zinc-100">Your configuration</h3>
          <button
            type="button"
            onClick={onClose}
            className="p-1 rounded-md text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-300 hover:bg-zinc-100 dark:hover:bg-zinc-800 transition-colors"
            aria-label="Close configuration overview"
          >
            <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="px-4 py-3 max-h-[50vh] overflow-y-auto space-y-3">
          {totalPrice && (
            <div className="flex items-center justify-between py-2 px-3 rounded-xl bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200/60 dark:border-emerald-700/40">
              <span className="text-xs text-emerald-600 dark:text-emerald-400 font-medium uppercase tracking-wider">Total</span>
              <span className="text-lg font-bold text-emerald-700 dark:text-emerald-300">{totalPrice}</span>
            </div>
          )}

          {filteredSelections.length > 0 && (
            <div className="space-y-1">
              {filteredSelections.map(([key, value]) => {
                const { label, display } = resolveSelectionLabel(key, value);
                return (
                  <div key={key} className="flex justify-between items-center py-1.5 text-xs">
                    <span className="text-zinc-500 dark:text-zinc-400">{label}</span>
                    <span className="font-medium text-zinc-800 dark:text-zinc-200">{display}</span>
                  </div>
                );
              })}
            </div>
          )}

          <PricingDisplay productId={productId} selections={selections} />

          <div className="flex flex-wrap gap-2 pt-1">
            <button
              type="button"
              onClick={onDownloadSummary}
              className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium bg-zinc-100 dark:bg-zinc-800 text-zinc-700 dark:text-zinc-300 hover:bg-zinc-200 dark:hover:bg-zinc-700 transition-colors"
            >
              <svg className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3" />
              </svg>
              Export
            </button>
            <button
              type="button"
              onClick={onShare}
              disabled={shareStatus === 'saving'}
              className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 hover:bg-blue-100 dark:hover:bg-blue-900/30 transition-colors disabled:opacity-50"
            >
              <svg className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M7.217 10.907a2.25 2.25 0 100 2.186m0-2.186c.18.324.283.696.283 1.093s-.103.77-.283 1.093m0-2.186l9.566-5.314m-9.566 7.5l9.566 5.314m0 0a2.25 2.25 0 103.935 2.186 2.25 2.25 0 00-3.935-2.186zm0-12.814a2.25 2.25 0 103.933-2.185 2.25 2.25 0 00-3.933 2.185z" />
              </svg>
              {shareStatus === 'copied' ? 'Copied!' : shareStatus === 'saving' ? 'Saving...' : 'Share'}
            </button>
            <button
              type="button"
              onClick={onReset}
              className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-900/30 transition-colors"
            >
              <svg className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182M2.985 14.652" />
              </svg>
              Reset
            </button>
            {modelUrl && (
              <ARPreview modelUrl={modelUrl} productName={productName} />
            )}
          </div>

          <ExportTools
            productName={productName}
            configSummary={Object.fromEntries(
              filteredSelections.map(([k, v]) => {
                const { label, display } = resolveSelectionLabel(k, v);
                return [label, display];
              })
            )}
          />
        </div>
      </div>
    </div>
  );
}
